// src/hooks/useJobLogs.ts
// FULL FILE — new file. Requires: npm install socket.io-client

import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import env from "configs"; // replace with wherever your API_BASE_URL constant lives

export interface JobLogEntry {
  type: "log" | "pdf" | "status";
  message: string;
  timestamp?: string;
  schoolId?: string;
  role?: string;
  staffId?: string;
  schoolName?: string;
  pdfUrl?: string;
}

interface JobStatus {
  status: "idle" | "active" | "completed" | "failed";
  results?: unknown[];
  error?: string;
}

export function useJobLogs(jobId: string | null) {
  const [logs, setLogs] = useState<JobLogEntry[]>([]);
  const [letters, setLetters] = useState<{ role?: string; schoolName?: string; pdfUrl: string }[]>(
    []
  );
  const [jobStatus, setJobStatus] = useState<JobStatus>({ status: "idle" });
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!jobId) return;

    setLogs([]);
    setLetters([]);
    setJobStatus({ status: "idle" });

    const socket = io(new URL(env.API_BASE_URL).origin, {
      path: "/api/socket.io", // must match the server's initSocket path exactly
      // no transports override — let socket.io negotiate polling -> websocket
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ client socket connected", socket.id);
      console.log("jobId", jobId);
      setJobStatus((prev) => ({ ...prev, status: "active" }));
      socket.emit("job:subscribe", jobId);
    });

    socket.on("connect_error", (err) => {
      console.error("❌ connect_error", err.message);
    });

    socket.onAny((event, ...args) => {
      console.log("📥 received event:", event, args);
    });

    socket.on("job:log", (data: JobLogEntry) => {
      setLogs((prev) => [...prev, data]);
      if (data.type === "pdf" && data.pdfUrl) {
        setLetters((prev) => [
          ...prev,
          { role: data.role, schoolName: data.schoolName, pdfUrl: data.pdfUrl as string },
        ]);
      }
    });

    socket.on("job:status", (data: { status: string }) => {
      console.log("job:status", data);
      setJobStatus((prev) => ({ ...prev, status: data.status as JobStatus["status"] }));
    });

    socket.on("job:completed", (data: { results: unknown[] }) => {
      console.log("job:completed", data);
      setJobStatus({ status: "completed", results: data.results });
    });

    socket.on("job:failed", (data: { error: string }) => {
      setJobStatus({ status: "failed", error: data.error });
    });

    return () => {
      socket.emit("job:unsubscribe", jobId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [jobId]);

  return { logs, letters, jobStatus };
}